import React, { useRef, useMemo, useState } from 'react';
import { useUi, useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType, SUFFIXES } from '@/types';
import { AudioPlayer, CdnMedia } from '@/components';
import { Toolbar } from '../components/Toolbar';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { WhitePanel } from '../components/WhitePanel';
import { GrabGameSuffixes } from '../components/GrabGameSuffixes';
import { findSoundsInString } from '@/utils/sounds';

interface SpellingReviewRootWordsAndSuffixesStepProps
  extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
  suffixes: Array<string>;
}

const SpellingReviewRootWordsAndSuffixesStep = ({
  activity,
  mode,
  wrapperClassName,
  index,
  total,
  word,
  suffixes,
}: SpellingReviewRootWordsAndSuffixesStepProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      usePanel
      title={`Click the button to hear the sound. Write the grapheme.`}
      showTitle={true}
      showActivityPagination={false}
    >
      <Toolbar
        onPlay={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        hintPlay={!playedOnce}
        playDisabled={!playerReady}
      />

      <WhitePanel mode="basic" kind="fill" color="paper">
        <CdnMedia
          className="block mx-auto"
          symbol={word}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />

        <GrabGameSuffixes
          word={word}
          suffixes={suffixes}
          suffixEngine="correctAnswers"
          correctAnswers={suffixes.map((suffix) => `${word}${suffix}`)}
        />
      </WhitePanel>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </ActivityStep>
  );
};

export const SpellingReviewRootWordsAndSuffixes = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words) {
    throw new Error('Words are required for this activity');
  }

  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { words, sounds } = activity;

  const suffixes = useMemo(
    () => findSoundsInString(SUFFIXES, sounds),
    [sounds],
  );

  return (
    <Activity
      steps={[
        ...(mode === 'TEACH'
          ? [
              <ActivityTitleStep
                key={`${activity.id}-title`}
                activity={activity}
                mode={mode}
                wrapperClassName={wrapperClassName}
                isPreview={isPreview}
                index={0}
                total={words.length + 1}
              />,
            ]
          : []),
        ...words.flatMap((word, index) => [
          <SpellingReviewRootWordsAndSuffixesStep
            key={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={words.length + 1}
            word={word}
            suffixes={suffixes}
          />,
        ]),
      ]}
    />
  );
};
